/**
 * ALL-7273: the source-reported observation time for wallet activity.
 *
 * `WalletActivity.asOf` is opaque evidence supplied by the activity source. It
 * is not a freshness claim: this note only reports the value the source gave
 * us, or says that no observation time is available. The raw string is
 * rendered exactly as supplied (trimmed only to decide whether it is blank),
 * inside a plain span — never a `<time>` element and never parsed into a local
 * date — so a malformed or unusual value cannot be presented as a trustworthy
 * timestamp. No controls, network calls or clock reads belong here.
 */

export interface WalletActivityObservationTimeProps {
  /** The source-reported observation time, exactly as received. */
  asOf?: string | null;
  /** Whether an activity observation exists at all; false renders nothing. */
  observed: boolean;
}

export function WalletActivityObservationTime({
  asOf,
  observed,
}: WalletActivityObservationTimeProps) {
  if (!observed) return null;

  const reported = typeof asOf === "string" && asOf.trim() !== "";

  return (
    <p
      role="note"
      aria-label="Wallet activity observation time"
      data-testid="wallet-activity-observation-time"
      data-observation-time={reported ? "reported" : "unavailable"}
      className="min-w-0 break-words border-t border-line px-5 py-3 text-xs text-muted"
    >
      {reported ? (
        <>
          Reported observation time:{" "}
          <span className="break-words whitespace-pre-wrap">{asOf}</span>
        </>
      ) : (
        "Observation time unavailable"
      )}
    </p>
  );
}
